import { useMemo, ReactNode } from 'react';

import round from 'lodash/round';
import pluralize from 'pluralize';

import {
  mergeSx,
  SxProps,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import type { BillingV1FeatureEntityUsage } from '@cast/types';

import { FooterBox } from 'components/boxes';
import { NumberFormat } from 'components/number/NumberFormat';
import { PageFooter } from 'components/portals';

import { getEntityKey } from './UsageOverTimeChart';

type MetricsProps = {
  title: ReactNode;
  value: number;
  sx?: SxProps;
  testId?: string;
};

const Metrics = ({ sx, title, value, testId }: MetricsProps) => {
  return (
    <Stack
      direction="row"
      flexWrap="nowrap"
      alignItems="center"
      px={12}
      gap={8}
      sx={mergeSx(
        {
          minHeight: '22px',
          borderLeft: `1px solid ${tokens.theme.border.structural.neutral}`,
        },
        sx
      )}
    >
      <Typography variant="L10R" color={tokens.theme.fg.structural.secondary}>
        {title}
      </Typography>
      <Typography
        color={tokens.theme.fg.structural.primary}
        variant="h6"
        fontSize={14}
        lineHeight={22}
      >
        <NumberFormat
          value={round(value, 2)}
          thousandSeparator
          displayType="text"
          data-testid={testId}
        />
      </Typography>
    </Stack>
  );
};

type PlatformUsageFooterProps = {
  selectedRows: string[];
  entities: BillingV1FeatureEntityUsage[];
  unit: string;
  activeTab: 'perCluster' | 'perOrganization' | 'daily';
};

export const PlatformUsageFooter = ({
  selectedRows,
  entities,
  unit,
  activeTab,
}: PlatformUsageFooterProps) => {
  const totals = useMemo(() => {
    if (!selectedRows.length) {
      return null;
    }

    const selectedEntities = entities.filter((entity) =>
      selectedRows.includes(getEntityKey(entity))
    );

    const totalUsage = selectedEntities.reduce(
      (sum, entity) => sum + (entity.usage ?? 0),
      0
    );

    return {
      clusterCount: selectedEntities.length,
      totalUsage,
    };
  }, [selectedRows, entities]);

  if (
    !selectedRows.length ||
    !totals ||
    (activeTab !== 'perCluster' && activeTab !== 'perOrganization')
  ) {
    return null;
  }

  const entityLabel =
    activeTab === 'perOrganization' ? 'organization' : 'cluster';

  return (
    <PageFooter>
      <FooterBox>
        <Stack
          direction="row"
          flexWrap="nowrap"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            width: '100%',
            minHeight: '32px',
          }}
        >
          <Typography variant="L12R">Total for selection</Typography>
          <Stack direction="row" flexWrap="nowrap" alignItems="center">
            <Metrics
              title={pluralize(entityLabel, totals.clusterCount)}
              value={totals.clusterCount}
              testId={`selected-${entityLabel}s`}
            />
            <Metrics
              title={`Usage (${unit})`}
              value={totals.totalUsage}
              testId="total-usage"
            />
          </Stack>
        </Stack>
      </FooterBox>
    </PageFooter>
  );
};
